import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';

interface PoweredByBadgeProps {
  branchId?: string;
  restaurantId?: string;
  className?: string;
  variant?: 'light' | 'dark';
  /** Preloaded branding tier to avoid extra DB call */
  preloadedBrandingTier?: string;
}

/**
 * Shows "Powered by Ripplefy" badge based on branding_tier subscription level.
 * 
 * Branding tier levels:
 * - 'none' or missing: Show full branding with logo
 * - 'basic': Show text-only branding
 * - 'advanced': Show minimal branding (small text)
 * - 'enterprise' or 'white_label': No branding (fully white-labeled)
 */
export function PoweredByBadge({ 
  branchId, 
  restaurantId, 
  className = '',
  variant = 'light',
  preloadedBrandingTier,
}: PoweredByBadgeProps) {
  const { t } = useTranslation();
  
  // Only fetch if no preloaded tier is provided
  const { data: fetchedBrandingTier } = useQuery({
    queryKey: ['branding-tier', branchId, restaurantId],
    queryFn: async () => {
      let targetRestaurantId = restaurantId;
      
      // If we have branchId but no restaurantId, fetch it
      if (branchId && !restaurantId) {
        const { data: branch } = await supabase
          .from('branches')
          .select('restaurant_id')
          .eq('id', branchId)
          .single();
        
        if (branch) {
          targetRestaurantId = branch.restaurant_id;
        }
      }
      
      if (!targetRestaurantId) {
        return 'none'; // Show branding by default
      }
      
      // Get subscription for this restaurant
      const { data: subscription } = await supabase
        .from('tenant_subscriptions')
        .select('cached_features')
        .eq('restaurant_id', targetRestaurantId)
        .single();
      
      if (!subscription?.cached_features) {
        return 'none'; // Show branding by default
      }
      
      const features = subscription.cached_features as Record<string, unknown>;
      const tier = features.branding_tier as string;
      
      return tier || 'none';
    },
    enabled: !preloadedBrandingTier && !!(branchId || restaurantId),
    staleTime: 10 * 60 * 1000, // 10 minutes - branding rarely changes
  });
  
  // Use preloaded tier if available, otherwise use fetched
  const brandingTier = preloadedBrandingTier || fetchedBrandingTier;
  
  // Enterprise tier: completely hide branding
  if (brandingTier === 'enterprise' || brandingTier === 'white_label' || brandingTier === 'full') {
    return null;
  }
  
  const textColor = variant === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const linkColor = variant === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700';
  
  // Advanced tier: minimal text
  if (brandingTier === 'advanced' || brandingTier === 'partial') {
    return (
      <div className={`text-center py-2 ${className}`}>
        <span className={`text-xs ${textColor}`}>
          <a 
            href="https://ripplefy.app" 
            target="_blank" 
            rel="noopener noreferrer"
            className={linkColor}
          >
            Ripplefy
          </a>
        </span>
      </div>
    );
  }
  
  // Basic tier: text branding
  if (brandingTier === 'basic' || brandingTier === 'logo_removal') {
    return (
      <div className={`text-center py-3 ${className}`}>
        <span className={`text-sm ${textColor}`}>
          {t('branding.powered_by', 'Powered by')}{' '}
          <a 
            href="https://ripplefy.app" 
            target="_blank" 
            rel="noopener noreferrer"
            className={`font-medium ${linkColor}`}
          >
            Ripplefy
          </a>
        </span>
      </div>
    );
  }
  
  // None tier (default): full branding with emphasis
  return (
    <div className={`text-center py-4 border-t ${variant === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'} ${className}`}>
      <div className="flex items-center justify-center gap-2">
        <span className={`text-sm ${textColor}`}>
          {t('branding.powered_by', 'Powered by')}
        </span>
        <a 
          href="https://ripplefy.app" 
          target="_blank" 
          rel="noopener noreferrer"
          className={`font-semibold text-base ${linkColor} flex items-center gap-1`}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          Ripplefy
        </a>
      </div>
      <p className={`text-xs mt-1 ${textColor}`}>
        {t('branding.tagline', 'Restaurant Management Made Simple')}
      </p>
    </div>
  );
}

export default PoweredByBadge;
